<template>
  <div class="entry-summary">
    <h3 class="block-title mt-15">{{ $t('cart.entries') }}</h3>
    <bsi-entry-countdown-timer-side v-if="entryCountdownTimer" />
    <div v-if="entries.length">
      <ul class="entries-list">
        <li class="entry" v-for="(entry, index) in entries" :key="index">
          <div v-if="entry.entryId">
            <div class="">
              <ul class="entry-details">
                <li class="d-flex justify-content-between">
                  <span class="entry-info">
                    <strong class="d-block">{{ entry.race.name }}</strong>
                  </span>
                  <span class="remove-entry" @click="removeEntry(entry)">
                    <i class="fas fa-times-circle fa-sm remove-icon"></i> {{ $t('general.remove') }}
                  </span>
                </li>
              </ul>
              <div>
                <div class="d-flex justify-content-between">
                  <span class="entry-participant"
                    >{{ entry.participant.name }} (<strong>{{ entry.participant.uid }}</strong
                    >)</span
                  >
                  <strong class="entry-fee">{{ entry.fee.amount | price(entry.fee.currency) }}</strong>
                </div>
              </div>
            </div>
          </div>
          <div v-if="entry.teamEntryId">
            <div class="d-flex justify-content-between">
              <div>
                <strong class="d-block mb-2">{{ getTeamEntryName(entry) }}</strong>
                <strong class="race">{{ entry.race.name }}</strong>
              </div>
              <div class="text-right">
                <span class="remove-cart-item" @click="removeEntry(entry)">
                  <i class="fas fa-times-circle fa-sm remove-icon"></i> {{ $t('teamEntry.remove') }}
                </span>
                <button class="btn btn-primary btn-gradient mt-2 mb-3" @click="modifyTeamEntry(entry)">
                  {{ $t('general.edit') }}
                </button>
                <strong class="team-entry-size-error" v-if="!isTeamFull(entry)"
                  >{{ $t('teamEntry.invalid') }}: ({{ entry.memberEntries.length }}/{{ getMinTeamSize(entry) }})</strong
                >
              </div>
            </div>
            <ul class="pl-4 mt-3">
              <li
                class="team-entry mb-3 d-flex flex-column flex-sm-row justify-content-between"
                v-for="teamEntry in entry.memberEntries"
                :key="teamEntry.entryId"
              >
                <div>
                  <span class="entry-participant"
                    >{{ teamEntry.participant.name }} (<strong>{{ teamEntry.participant.uid }}</strong
                    >)</span
                  >
                  <br />
                </div>
                <div class="cart-item-price-wrapper d-flex justify-content-sm-end">
                  <strong class="price mr-0">{{ teamEntry.fee.amount | price(teamEntry.fee.currency) }}</strong>
                </div>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>

    <div class="entry-summary--empty mt-4 d-flex justify-content-center align-items-center" v-else>
      <span>{{ $t('cart.empty') }}</span>
    </div>

    <h3 class="block-title mt-15">{{ $t('cart.products') }}</h3>
    <div v-if="products.length">
      <ul class="product-list">
        <li
          class="product"
          :class="{ 'product-error': checkError(product.name, product.variant) }"
          v-for="(product, index) in products"
          :key="index"
        >
          <ul class="product-details mt-7">
            <li class="d-flex justify-content-between">
              <img class="product-image-cart" :src="product.image" />
              <a class="product-link" :href="`/products/${product.pid}`">
                <span class="entry-info product-cart" :title="product.name">
                  {{ product.name }} <span class="product-variant-cart">{{ product.variant.name }}</span>
                </span></a
              >
              <strong class="entry-fee">{{ product.price.amount | price(product.price.currency) }}</strong>
              <span class="remove-entry ml-1" @click="removeProduct(product)">
                <i class="fas fa-times-circle fa-sm remove-icon"></i>
              </span>
            </li>
          </ul>
          <span class="entry-participant"
            ><span v-if="product.participantId">{{ product.participantName }}({{ product.participantId }})</span></span
          >
          <div v-if="needsEntrySelection(product)" class="product-entry-pending mt-2">
            {{ $t('cart.entryProduct.pending') }}
          </div>
        </li>
      </ul>
    </div>

    <div class="entry-summary--empty mt-4 d-flex justify-content-center align-items-center" v-else>
      <span>{{ $t('cart.empty') }}</span>
    </div>

    <BsiCoupons></BsiCoupons>

    <div class="entries-price-sum d-flex justify-content-between">
      <span>{{ $t('cart.total') }}:</span>
      <span class="price-sum">{{ totalAmount | price(currency) }}</span>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import * as types from '@/types'
import _ from 'lodash'
import getFormatedDate from '@/mixins/getFormatedDate'
import { isEntryBoundProduct } from '@/modules/cart/utilities/entryProduct'
import BsiCoupons from '@/modules/cart/components/Coupons/BsiCoupons'
import BsiEntryCountdownTimerSide from '@/modules/entry/components/BsiEntryCountdownTimerSide'
import { raceItemId, productItemId, eventCategory2, pushEcommerceEvent } from '@/modules/global/utilities/ecommerce'

import { formatUserName } from '@/modules/profile/utilities/userName'
export default {
  name: 'BsiCartSummary',
  mixins: [getFormatedDate],
  components: {
    BsiCoupons,
    BsiEntryCountdownTimerSide
  },
  computed: {
    ...mapState({
      entries: state => state.cart.cart.entries,
      products: state => state.cart.cart.products,
      profile: state => state.profile,
      entryCountdownTimer: state => state.cart.entryCountdownTimer
    }),
    ...mapGetters(['totalAmount', 'getProductErrors']),
    getParticipant() {
      return `${formatUserName(this.profile.user)} (${this.profile.userId})`
    },
    currency() {
      return process.env.VUE_APP_CURRENCY
    }
  },
  methods: {
    needsEntrySelection(product) {
      return isEntryBoundProduct(product) && !product.entryId
    },
    checkError(name, variant) {
      if (variant) {
        name = `${name} - ${variant.name}`
      }
      console.log(name)
      return this.getProductErrors.includes(name)
    },
    isTeamFull(entry) {
      return entry.teamSizes.includes(entry.memberEntries.length)
    },
    getMinTeamSize(entry) {
      return Math.min(...entry.teamSizes)
    },
    getTeamEntryName(entry) {
      return _.result(_.find(entry.teamFields, { id: 'csapatnev' }), 'text')
    },
    modifyTeamEntry(entry) {
      this.$store.commit(types.SET_SELECTED_ENTRY, entry)
      this.$router.push({
        name: 'modify-team-entry',
        params: { entryId: entry.memberEntries[0].entryId },
        query: { page: 'cart' }
      })
    },
    removeEntry(entry) {
      this.$bvModal
        .msgBoxConfirm(this.$t('cart.item.entry.delete.info'), {
          title: this.$t('cart.item.entry.delete.confirm'),
          centered: true,
          okTitle: this.$t('general.ok'),
          cancelTitle: this.$t('general.cancel')
        })
        .then(isConfirmed => {
          if (isConfirmed) {
            this.sendEcommerceRemoveEntryFromCart(entry)
            this.removeRelatedProduct(entry.entryId)
            if (entry.teamEntryId) {
              this.$store
                .dispatch(types.REMOVE_TEAM_ENTRY_FROM_CART_REQUEST, entry)
                .then(() => {
                  this.$noty.success(this.$t('cart.item.entry.delete.success'))
                })
                .catch(() => {
                  this.$noty.error(this.$t('cart.item.entry.delete.error'))
                })
            } else {
              this.$store
                .dispatch(types.REMOVE_ENTRY_FROM_CART_REQUEST, entry)
                .then(() => {
                  this.$noty.success(this.$t('cart.item.entry.delete.success'))
                })
                .catch(() => {
                  this.$noty.error(this.$t('cart.item.entry.delete.error'))
                })
            }
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    removeProduct(item) {
      this.sendEcommerceRemoveFromCart(item)
      this.$store.commit(types.REMOVE_PRODUCT_FROM_CART, item)
    },
    removeRelatedProduct(entryId) {
      this.products.forEach(product => {
        if (product.entryId && product.entryId == entryId) {
          this.removeProduct(product)
        }
      })
    },
    transformEntryToDataLayer(entry) {
      const data = {
        item_id: raceItemId(entry.race.id),
        item_name: entry.race.name,
        entry_id: entry.entryId,
        currency: entry.fee.currency,
        item_category: 'entry',
        item_category2: eventCategory2(entry.race.eventId),
        price: entry.fee.amount,
        quantity: 1
      }
      return data
    },
    sendEcommerceRemoveEntryFromCart(entry) {
      const entryClone = { ...entry }
      const ecommerce = {
        event: 'remove_from_cart',
        ecommerce: {
          items: []
        }
      }
      if (entryClone.memberEntries) {
        ecommerce.ecommerce.items = entryClone.memberEntries.map(this.transformEntryToDataLayer)
      } else {
        ecommerce.ecommerce.items.push(this.transformEntryToDataLayer(entryClone))
      }
      this.sendEcommerce(ecommerce)
    },
    sendEcommerceRemoveFromCart(item) {
      const ecommerce = {
        event: 'remove_from_cart',
        ecommerce: {
          items: [
            {
              item_id: productItemId(item.pid),
              item_name: item.name,
              currency: item.price.currency,
              item_category: 'product',
              item_category2: eventCategory2(item.eventId),
              price: item.price.amount,
              quantity: 1
            }
          ]
        }
      }
      this.sendEcommerce(ecommerce)
    },
    sendEcommerce(ecommerce) {
      pushEcommerceEvent(ecommerce)
    }
  }
}
</script>

<style lang="scss" scoped>
.entry-summary {
  padding: 15px 15px 30px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
}

.entries-list,
.product-list {
  padding: 0;

  .entry,
  .product {
    margin-bottom: 5px;
  }
}

.entries-price-sum {
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid $silver;

  span {
    display: block;
  }

  .price-sum {
    color: $dark-blue;
    font-size: 20px;
    line-height: 16.5px;
    font-weight: bold;
  }
}

.entry-details {
  margin-top: 15px;
  color: $greyish-brown;
}

.remove-entry {
  cursor: pointer;

  .remove-icon {
    color: $azure;
  }
}

.remove-cart-item {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.team-entry {
  .entry-participant,
  .entry-participant-birth {
    font-size: 0.8em !important;
  }
}

.entry-participant,
.entry-participant-birth {
  font-size: 0.8em !important;
}

.team-entry-size-error {
  color: #eb1c2d;
}
</style>

<style lang="scss">
.entry-summary {
  .coupons {
    h3 {
      margin-bottom: 8px !important;
    }
  }

  .coupon__item {
    flex-direction: row !important;
  }

  .coupon__price-wrapper {
    flex-direction: column;
    margin-right: 0;

    .coupon__discount {
      margin-right: 0 !important;
    }
  }

  .coupon__participant {
    display: block;
  }
}

.product-image-cart {
  width: 40px;
  height: 40px;
  margin-right: 5px;
  padding: 3px;
}

.product-link {
  color: black;
  text-decoration: none !important;
}

.product-error {
  animation: colorChange 2.5s ease-in-out;
}

@keyframes colorChange {
  0% {
    background-color: white;
  }

  50% {
    background-color: #f44646;
  }

  100% {
    background-color: white;
  }
}
</style>
